import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { baseUrl } from 'Api';
import { AppContext, AuthContext } from 'Context';
import { SettingsPage } from '../components/page';
import GiftsTab from './GiftsTab';
import styles from './index.module.css';

const rowName = (row) => `${row?.firstname || ''} ${row?.lastname || ''}`.trim() || '—';

// Users who signed up through the referral link. Defined inline (rather than a
// sibling file) so it always resolves in the running dev server.
function SignupsTab({ loading, error, users }) {
  if (loading) return <p className={styles.stateText}>Loading signups...</p>;
  if (error) return <p className={styles.stateText}>Couldn&apos;t load signups. Please try again later.</p>;
  if (!users || users.length === 0) return <p className={styles.stateText}>No one has signed up through your link yet.</p>;

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Mobile</th>
            <th>Plan</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, idx) => (
            <tr key={user?.id || `${user?.email || 'user'}-${idx}`}>
              <td className={styles.nameCell}>{rowName(user)}</td>
              <td className={styles.emailCell}>{user?.email || '—'}</td>
              <td className={styles.emailCell}>{user?.mobile || '—'}</td>
              <td>{user?.plan_name || 'Free'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Settings → Referrals. Shows the affiliate's shareable referral link (built
// from referral_key) with a copy option, then two tabs: Signups and Gifts.
// Data comes from getMyGiftReferralDetails. The Signups API is wired later.
export const ReferralsSettings = () => {
  const { getToken } = useContext(AuthContext);
  const { setmessaged, setmangerFunctionData, setLineReq } = useContext(AppContext);

  // Hide the SettingsPage title divider — this page has no page title, so the
  // <hr> would just float awkwardly at the top.
  useEffect(() => {
    if (setLineReq) setLineReq(true);
    return () => { if (setLineReq) setLineReq(false); };
  }, [setLineReq]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [details, setDetails] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('signups');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const token = await getToken();
        const { data } = await axios.post(`${baseUrl}/getMyReferralDetails`, { token });
        if (!mounted) return;
        if (data?.success) {
          setDetails(data?.referral_details || null);
          setGifts(Array.isArray(data?.gifts) ? data.gifts : []);
          setUsers(Array.isArray(data?.users) ? data.users : []);
        } else {
          setError(true);
        }
      } catch (e) {
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [getToken]);

  const referralKey = details?.referral_key || '';
  const referralLink = referralKey ? `https://www.projectorstream.com/referral/${referralKey}` : '';

  const copyLink = () => {
    if (!referralLink) return;
    const ta = document.createElement('textarea');
    ta.value = referralLink;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (setmessaged) setmessaged('Referral link copied to clipboard');
    if (setmangerFunctionData) setmangerFunctionData(true);
  };

  return (
    <SettingsPage>
      <div className={styles.page}>
        {/* Referral link card */}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <p className={styles.cardTitle}>Your Referral Link</p>
            <p className={styles.cardSub}>Share this link — people can sign up or receive a gift through it</p>
          </div>

          {loading ? (
            <p className={styles.stateText}>Loading your referral details...</p>
          ) : error || !referralKey ? (
            <p className={styles.stateText}>Couldn&apos;t load your referral details. Please try again later.</p>
          ) : (
            <div className={styles.linkRow}>
              <div className={styles.keyChip}>
                <span className={styles.keyLabel}>Referral Key</span>
                <span className={styles.keyValue}>{referralKey}</span>
              </div>
              <div className={styles.linkBox}>
                <span className={styles.linkText}>{referralLink}</span>
                <button
                  type="button"
                  className={`${styles.copyBtn} ${copied ? styles.copyBtnCopied : ''}`}
                  onClick={copyLink}
                >
                  {copied ? (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      Copy Link
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tabbed card: Signups | Gifts */}
        <div className={styles.card}>
          <div className={styles.tabBar}>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'signups' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('signups')}
            >
              Signups
              {!loading && <span className={styles.tabCount}>{users.length}</span>}
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'gifts' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('gifts')}
            >
              Gifts
              {!loading && <span className={styles.tabCount}>{gifts.length}</span>}
            </button>
          </div>

          {activeTab === 'signups' ? (
            <SignupsTab loading={loading} error={error} users={users} />
          ) : (
            <GiftsTab loading={loading} error={error} gifts={gifts} />
          )}
        </div>
      </div>
    </SettingsPage>
  );
};

export default ReferralsSettings;
